import React, { useState, useContext, useEffect } from 'react';
import { Grid, TextField, DialogActions, Button, CircularProgress, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import styles from './styles.module.css';
import { AlertMessagesContext } from 'react-alert-messages';
import ItemService from 'services/ItemService';
import { useTranslation } from 'react-i18next';

const formatToDateTimeLocal = (dateString) => {
  if (!dateString) return '';
  try {
    const cleanDateStr = dateString.endsWith('Z') ? dateString.slice(0, -1) : dateString;
    const d = new Date(cleanDateStr);
    if (isNaN(d.getTime())) return '';
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const hours = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  } catch (e) {
    console.error(e);
    return '';
  }
};

export default function EditBatchExpiryDialog({ batch, handleClose, handleSuccess }) {
  const { t: tCommon } = useTranslation('common');
  const [isLoading, setLoading] = useState(false);
  const { postAlertMessage } = useContext(AlertMessagesContext);

  const [expiryDate, setExpiryDate] = useState(() => formatToDateTimeLocal(batch?.expiry_date));
  const [batchName, setBatchName] = useState(batch?.batch_name || '');

  useEffect(() => {
    setExpiryDate(formatToDateTimeLocal(batch?.expiry_date));
    setBatchName(batch?.batch_name || '');
  }, [batch]);

  const handleUpdateBatch = async () => {
    setLoading(true);
    try {
      let finalDateStr = '';
      if (expiryDate) {
        finalDateStr =
          expiryDate.includes(':') && expiryDate.split(':').length === 2 ? `${expiryDate}:00Z` : `${expiryDate}Z`;
      }
      const updated = await ItemService.updateStockBatch(batch.id, {
        expiry_date: finalDateStr,
        batch_name: batchName,
      });
      postAlertMessage({
        type: 'success',
        text: tCommon('Batch expiry date updated successfully.') || 'Batch expiry date updated successfully.',
      });
      handleSuccess(updated);
    } catch (error) {
      postAlertMessage({
        type: 'error',
        text: error?.message || tCommon('Failed to update batch expiry date.') || 'Failed to update batch expiry date.',
      });
    } finally {
      setLoading(false);
    }
  };

  if (!batch) return null;

  return (
    <div className={styles.popUp}>
      <div className={styles.contentWrapper}>
        <div className={styles.headSec}>
          <h2 className={styles.editTitle}>{tCommon('Edit Batch Expiry Date')}</h2>
          <IconButton onClick={handleClose} disabled={isLoading}>
            <CloseIcon />
          </IconButton>
        </div>

        <div className={styles.inputSec}>
          <Grid container className={styles.inputRow} alignItems="center">
            <Grid item xs={4} sm={4} className={styles.inputLabelContainer}>
              <h3 className={styles.inputLabel}>{tCommon('Batch ID')}</h3>
            </Grid>
            <Grid item xs={8} sm={8}>
              <TextField variant="outlined" size="small" fullWidth value={batch.id} disabled />
            </Grid>
          </Grid>

          <Grid container className={styles.inputRow} alignItems="center">
            <Grid item xs={4} sm={4} className={styles.inputLabelContainer}>
              <h3 className={styles.inputLabel}>{tCommon('Batch Name')}</h3>
            </Grid>
            <Grid item xs={8} sm={8}>
              <TextField
                variant="outlined"
                size="small"
                fullWidth
                value={batchName}
                onChange={(e) => setBatchName(e.target.value)}
                disabled={isLoading}
              />
            </Grid>
          </Grid>

          <Grid container className={styles.inputRow} alignItems="center">
            <Grid item xs={4} sm={4} className={styles.inputLabelContainer}>
              <h3 className={styles.inputLabel}>{tCommon('Stock')}</h3>
            </Grid>
            <Grid item xs={8} sm={8}>
              <TextField
                variant="outlined"
                size="small"
                fullWidth
                value={Number(batch.stock || 0).toFixed(3)}
                disabled
              />
            </Grid>
          </Grid>

          <Grid container className={styles.inputRow} alignItems="center">
            <Grid item xs={4} sm={4} className={styles.inputLabelContainer}>
              <h3 className={styles.inputLabel}>{tCommon('Expiry Date')}</h3>
            </Grid>
            <Grid item xs={8} sm={8}>
              <TextField
                variant="outlined"
                size="small"
                fullWidth
                type="datetime-local"
                value={expiryDate}
                onChange={(e) => setExpiryDate(e.target.value)}
                disabled={isLoading}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
        </div>

        <div className={styles.footSec}>
          <DialogActions>
            <Button
              variant="contained"
              color="secondary"
              onClick={handleClose}
              disabled={isLoading}
              className={styles.closeBtn}
            >
              {tCommon('Close')}
            </Button>
            <Button
              variant="contained"
              color="primary"
              style={{ backgroundColor: '#00a65a' }}
              disabled={isLoading}
              onClick={handleUpdateBatch}
            >
              {isLoading ? (
                <>
                  <CircularProgress size={20} color="inherit" style={{ marginRight: '10px' }} />
                  {tCommon('Updating...')}
                </>
              ) : (
                tCommon('Update')
              )}
            </Button>
          </DialogActions>
        </div>
      </div>
    </div>
  );
}
